<template>
  <transition name="fade">
    <div v-if="visible" class="maintenance-banner" role="status" aria-live="polite">
      <div class="banner-content">
        <div class="banner-text">{{ messageToShow }}</div>
        <div class="banner-links">
          <router-link to="/feedback" class="feedback-link">Contact us</router-link>
          <router-link v-if="isLoginDisabled" to="/puzzle" class="game-link">🎮 Play Game</router-link>
        </div>
        <CloseButton @close="dismiss" aria-label="Dismiss maintenance notice" />
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import apiService, { type MaintenanceSettings } from '../services/apiService'
import CloseButton from './CloseButton.vue'

const visible = ref(false)
const message = ref('')
const isLoginDisabled = ref(false)

const DISMISS_KEY = 'maintenance_banner_dismissed_at'

const messageToShow = computed(() => message.value || 'The site is under maintenance. Some features may be unavailable.')

function dismiss() {
  visible.value = false
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())) } catch {}
}

async function fetchMaintenance() {
  try {
    const data: MaintenanceSettings = await apiService.getMaintenance()
    if (data?.enabled) {
      message.value = data.message || ''
      // Always show when maintenance is enabled to avoid confusion after toggling
      visible.value = true
    } else {
      visible.value = false
    }
  } catch {
    // Silently ignore if endpoint not available
  }
}

async function fetchLoginStatus() {
  try {
    const loginStatus = await apiService.get('/login-status', undefined, { skipAuth: true })
    if (loginStatus?.login_disabled) {
      // Don't show banner for login disabled - handled in AuthView
      isLoginDisabled.value = true
      visible.value = false
    }
  } catch {
    // Silently ignore if endpoint not available
  }
}

onMounted(() => {
  fetchMaintenance()
  fetchLoginStatus()
})
</script>

<style scoped>
.maintenance-banner {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  max-width: min(520px, 90vw);
  background: rgba(220, 53, 69, 0.15);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1), 0 0 20px rgba(220, 53, 69, 0.1); /* Add subtle glow */
  z-index: 2000;
  margin-top: 1rem; /* Add top margin for separation */
  border-top: 2px solid rgba(220, 53, 69, 0.3); /* Add subtle top border */
}
.banner-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  padding-top: 3rem; /* Add top padding to avoid close button overlap */
  position: relative;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(220, 53, 69, 0.1) 100%); /* Subtle gradient background */
}
.banner-content :deep(.close-button) {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
}
.banner-text {
  line-height: 1.35;
  opacity: 0.8;
  margin-bottom: 0.5rem;
}
.banner-links {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-start;
}
.feedback-link,
.game-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: underline;
  opacity: 0.8;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}
.feedback-link:focus,
.feedback-link:hover,
.game-link:focus,
.game-link:hover {
  color: rgba(255, 255, 255, 0.9);
  opacity: 1;
}
.game-link {
  background: rgba(34, 197, 94, 0.2);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  text-decoration: none;
  font-weight: 600;
}
.game-link:hover {
  background: rgba(34, 197, 94, 0.3);
  border-color: rgba(34, 197, 94, 0.5);
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>


